<template>
  <b-row>
    <b-col cols="12">
      <h4>
        <b-badge
          class="pointer mr-2"
          :variant="currentSelectDateFilter.today ? 'primary' : 'light'"
          :disabled="disabled"
          @click="setToday()"
        >
          Hoje
        </b-badge>
        <b-badge
          class="pointer mr-2"
          :variant="currentSelectDateFilter.sevenDays ? 'primary' : 'light'"
          :disabled="disabled"
          @click="setSevenDays()"
        >
          Últimos 7 dias
        </b-badge>
        <b-badge
          class="pointer mr-2"
          :variant="currentSelectDateFilter.month ? 'primary' : 'light'"
          :disabled="disabled"
          @click="setThisMonth()"
        >
          Este mês
        </b-badge>
        <b-badge
          class="pointer mr-2"
          :variant="currentSelectDateFilter.trimester ? 'primary' : 'light'"
          :disabled="disabled"
          @click="setTrimester()"
        >
          Este trimestre
        </b-badge>
        <b-badge
          class="pointer mr-2"
          :variant="currentSelectDateFilter.year ? 'primary' : 'light'"
          :disabled="disabled"
          @click="setYear()"
        >
          Este ano
        </b-badge>
        <b-badge
          class="pointer"
          :variant="currentSelectDateFilter.custom ? 'primary' : 'light'"
          :disabled="disabled"
          @click="setCustom()"
        >
          Personalizado
        </b-badge>
      </h4>
    </b-col>
    <b-col cols="12" sm="6" class="mt-3">
      <b-form-datepicker
        v-model="fields.startDate"
        :disabled="filterDateByTags || disabled"
        reset-button
        close-button
        today-button
        :date-format-options="{
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
        }"
        placeholder="Data de início"
        label-close-button="Fechar"
        label-today-button="Hoje"
        label-reset-button="Limpar"
        label-help=""
        locale="pt-BR"
      />
    </b-col>
    <b-col cols="12" sm="6" class="mt-3">
      <b-form-datepicker
        v-model="fields.endDate"
        :disabled="filterDateByTags || disabled"
        reset-button
        close-button
        today-button
        :date-format-options="{
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
        }"
        placeholder="Data de fim"
        label-close-button="Fechar"
        label-today-button="Hoje"
        label-reset-button="Limpar"
        label-help=""
        locale="pt-BR"
      />
    </b-col>

    <b-button 
      variant="warning" 
      class="ml-3 mt-4" 
      @click="cleanFilters()"
    >
      <i class="fas fa-trash mr-2"></i>
      Limpar filtros de data
    </b-button>
  </b-row>
</template>

<script>
import { formatDateToYmd } from 'Utils/index';

export default {
  name: 'PeriodsFilters',
  emits: ['loaded'],
  props: {
    startDate: {
      type: String,
      required: false,
    },
    endDate: {
      type: String,
      required: false,
    },
    setDatesAutomatic: {
      type: Boolean,
      default: true,
    },
    /**
     * Defina qual será o período definido assim que o componente montar.
     * `today`, `sevenDays`, `month`, `trimester`, `year`, `custom`
     */
    periodToSetOnMount: {
      type: String, 
      default: 'today'
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    limparDados: {
      type: Boolean,
      default: false,
      required: false
    },
  },
  data() {
    return {
      fields: {
        startDate: '',
        endDate: '',
      },
      currentSelectDateFilter: {
        today: false,
        sevenDays: false,
        month: false,
        trimester: false,
        year: false,
        custom: false,
      }
    };
  },
  watch: {
    'fields.startDate': function () {
      this.$emit('update:startDate', this.fields.startDate);
    },
    'fields.endDate': function () {
      this.$emit('update:endDate', this.fields.endDate);
    },
    'limparDados': function () {
      this.cleanFilters()
    },
  },
  computed: {
    currentDate() {
      const currentDate = new Date();

      return formatDateToYmd(currentDate);
    },
    filterDateByTags() {
      let tagSelected = false;

      Object.keys(this.currentSelectDateFilter).forEach((key) => {
        if (this.currentSelectDateFilter[key]) tagSelected = true;
      });

      return !this.currentSelectDateFilter.custom && tagSelected;
    },
  },
  created() {
    this.getPeriodMethodToExecute();
  },
  methods: {
    cleanFilters(){
      this.currentSelectDateFilter.today = false;
      this.currentSelectDateFilter.sevenDays = false;
      this.currentSelectDateFilter.month = false;
      this.currentSelectDateFilter.trimester = false;
      this.currentSelectDateFilter.year = false;
      this.currentSelectDateFilter.custom = false;
      this.fields.startDate = '';
      this.fields.endDate = '';
    },
    setToday() {
      if (this.disabled) return;

      this.resetDateFiltersSelected('today');

      this.currentSelectDateFilter.today = !this.currentSelectDateFilter.today;

      if (!this.currentSelectDateFilter.today) return;

      this.fields.startDate = this.currentDate;
      this.fields.endDate = this.currentDate;
    },
    setSevenDays() {
      if (this.disabled) return;

      this.resetDateFiltersSelected('sevenDays');

      this.currentSelectDateFilter.sevenDays =
        !this.currentSelectDateFilter.sevenDays;

      if (!this.currentSelectDateFilter.sevenDays) return;

      const sevenDaysAgo = new Date(
        new Date().setDate(new Date().getDate() - 7)
      );

      this.fields.startDate = formatDateToYmd(sevenDaysAgo);
      this.fields.endDate = this.currentDate;
    },
    setThisMonth() {
      if (this.disabled) return;

      this.resetDateFiltersSelected('month');

      this.currentSelectDateFilter.month = !this.currentSelectDateFilter.month;

      if (!this.currentSelectDateFilter.month) return;

      const currentDate = new Date();

      const firstDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        1
      );

      const lastDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 1,
        0
      );

      this.fields.startDate = formatDateToYmd(firstDay);
      this.fields.endDate = formatDateToYmd(lastDay);
    },
    setTrimester() {
      if (this.disabled) return;

      this.resetDateFiltersSelected('trimester');

      this.currentSelectDateFilter.trimester =
        !this.currentSelectDateFilter.trimester;

      if (!this.currentSelectDateFilter.trimester) return;

      const currentDate = new Date();

      const firstDayOfTrimester = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 3,
        currentDate.getDate()
      );

      this.fields.startDate = formatDateToYmd(firstDayOfTrimester);
      this.fields.endDate = this.currentDate;
    },
    setYear() {
      if (this.disabled) return;

      this.resetDateFiltersSelected('year');

      this.currentSelectDateFilter.year = !this.currentSelectDateFilter.year;

      if (!this.currentSelectDateFilter.year) return;

      const currentDate = new Date();
      const firsDayYear = new Date(currentDate.getFullYear(), 0, 1);
      const lastDayYear = new Date(currentDate.getFullYear(), 11, 31);

      this.fields.startDate = formatDateToYmd(firsDayYear);
      this.fields.endDate = formatDateToYmd(lastDayYear);
    },
    setCustom() {
      if (this.disabled) return;

      this.resetDateFiltersSelected('custom');

      this.currentSelectDateFilter.custom =
        !this.currentSelectDateFilter.custom;
    },
    resetDateFiltersSelected(filterBeingEdited) {
      Object.keys(this.currentSelectDateFilter).forEach((key) => {
        if (key === filterBeingEdited) return;

        this.currentSelectDateFilter[key] = false;
        this.fields.startDate = '';
        this.fields.endDate = '';
      });
    },
    getPeriodMethodToExecute() {
      if (!this.setDatesAutomatic) return;

      const method = {
        'today': this.setToday,
        'sevenDays': this.setSevenDays,
        'month': this.setThisMonth,
        'trimester': this.setTrimester,
        'year': this.setYear,
        'custom': this.setCustom,
      };

      method[this.periodToSetOnMount]();
    }
  },
};
</script>

<style>
</style>